import { useState, useMemo, useCallback, useEffect } from "react";

import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";

import { apiSlice } from "app/apiSlice";
import DataGrid from "components/DataGrid";
import { getSelectGridColDefs } from "components/DataGrid/helpers/getSelectGridColDefs";
import { creatorsColDefs } from "features/creators/constants/creatorsColDefs";

const emptyArray = [];
const selectCreatorColDefs = getSelectGridColDefs(creatorsColDefs);

interface AdvancedCreatorSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCreator: (creatorId: string) => void;
  campaign_id?: string;
  excludeCreatorIds?: string[];
  preSelectedCreatorId?: string;
}

export const AdvancedCreatorSearchModal = ({
  isOpen,
  onClose,
  onSelectCreator,
  campaign_id,
  excludeCreatorIds = [],
  preSelectedCreatorId,
}: AdvancedCreatorSearchModalProps) => {
  const [selectedCreatorId, setSelectedCreatorId] = useState<string | null>(
    null,
  );
  const [showDataGrid, setShowDataGrid] = useState(false);

  // Only fetch data when modal is open to improve performance
  const { data: creatorsData, isLoading: isLoadingCreators }
    = apiSlice.useGetCreatorsQuery(
      {
        sortKey: "updated",
        sortValue: "-1",
      },
      {
        skip: !isOpen,
      },
    );

  const { assignedCreatorIds, isLoading: isLoadingAssignments }
    = apiSlice.useGetAssignmentsQuery(
      { campaign_id },
      {
        selectFromResult: ({ data, isLoading }) => ({
          assignedCreatorIds:
            data?.results?.map(
              (assignment: any) =>
                assignment?.user_id?._id ?? assignment?.user_id,
            ) || [],
          isLoading,
        }),
        skip: !campaign_id || !isOpen,
      },
    );

  const isLoading = isLoadingCreators || isLoadingAssignments;

  // Initialize selected creator state when modal opens
  useEffect(() => {
    if (isOpen && preSelectedCreatorId) {
      setSelectedCreatorId(preSelectedCreatorId);
    } else if (!isOpen) {
      setSelectedCreatorId(null);
    }
  }, [isOpen, preSelectedCreatorId]);

  // Defer DataGrid rendering until after modal is visible
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setShowDataGrid(true), 500);
      return () => clearTimeout(timer);
    } else {
      setShowDataGrid(false);
    }
  }, [isOpen]);

  const handleFirstDataRendered = useCallback(
    (event: any) => {
      // Pre-select the creator when data is first rendered
      if (preSelectedCreatorId) {
        const rowNode = event.api.getRowNode(preSelectedCreatorId);
        if (rowNode) {
          rowNode.setSelected(true);
        }
      }
    },
    [preSelectedCreatorId],
  );

  const handleSelectionChanged = useCallback((event: any) => {
    const selectedRows = event.api.getSelectedNodes();
    if (selectedRows.length > 0) {
      setSelectedCreatorId(selectedRows[0].id);
    } else {
      setSelectedCreatorId(null);
    }
  }, []);

  const isRowSelectable = useMemo(() => {
    return (rowNode: any) => {
      const creatorId = rowNode.id;
      return (
        !assignedCreatorIds.includes(creatorId)
        && !excludeCreatorIds.includes(creatorId)
      );
    };
  }, [assignedCreatorIds, excludeCreatorIds]);

  const handleSelectCreator = useCallback(() => {
    if (selectedCreatorId) {
      onSelectCreator(selectedCreatorId);
      onClose();
    }
  }, [selectedCreatorId, onSelectCreator, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Modal
      isOpen={isOpen}
      size="xl"
      toggle={handleClose}
      className="advanced-creator-search-modal"
      backdrop="static"
      keyboard={false}
      unmountOnClose={true}
      returnFocusAfterClose={true}
    >
      <ModalHeader toggle={handleClose}>Advanced Creator Search</ModalHeader>
      <ModalBody>
        {campaign_id && (
          <div className="mb-3">
            <small className="text-muted">
              Note: Creators without checkboxes are already assigned to this
              campaign.
            </small>
          </div>
        )}

        {showDataGrid ? (
          <DataGrid
            gridId="advanced-creators-select"
            columnDefs={selectCreatorColDefs}
            idField="_id"
            rowData={creatorsData?.results ?? emptyArray}
            loading={isLoading}
            height="500px"
            borders={true}
            onFirstDataRendered={handleFirstDataRendered}
            onSelectionChanged={handleSelectionChanged}
            rowSelection="single"
            isRowSelectable={isRowSelectable}
            quickFilter={true}
            appliedFilters={true}
            showLinkButton={false}
            HeaderActionElem={null}
          />
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "500px" }}
          >
            <div className="text-center">
              <Spinner className="mb-3" />
              <div className="text-muted">Loading advanced search...</div>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={handleClose}>
          Cancel
        </Button>
        <Button
          color="dark"
          disabled={!selectedCreatorId}
          onClick={handleSelectCreator}
        >
          Select Creator
        </Button>
      </ModalFooter>
    </Modal>
  );
};
